import React, { useCallback, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import type { AdhocPackOption } from '../../api/adhocApi';
import AdhocPaymentMethodButtons, { type PaymentProvider } from './AdhocPaymentMethodButtons';
import './landing.css';

const SCAN_MIN = 2;
const SCAN_MAX = 10;
const SCAN_PRICE_USD = 3;

export type { PaymentProvider };

export interface AdhocFamilyPackSectionProps {
  options: AdhocPackOption[];
  paying: boolean;
  payError: string | null;
  onPayPack: (scanCount: number, provider: PaymentProvider) => void;
}

function clampScanCount(value: number): number {
  return Math.min(SCAN_MAX, Math.max(SCAN_MIN, value));
}

function parseScanInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isInteger(n)) return null;
  return n;
}

const AdhocFamilyPackSection: React.FC<AdhocFamilyPackSectionProps> = ({
  paying,
  payError,
  onPayPack,
}) => {
  const [scanInput, setScanInput] = useState('2');
  const [inputError, setInputError] = useState<string | null>(null);

  const parsedScans = parseScanInput(scanInput);
  const displayPrice = parsedScans != null && parsedScans >= SCAN_MIN && parsedScans <= SCAN_MAX
    ? parsedScans * SCAN_PRICE_USD
    : null;

  const applyCount = useCallback((next: number) => {
    const clamped = clampScanCount(next);
    setScanInput(String(clamped));
    setInputError(null);
  }, []);

  const handleDecrement = useCallback(() => {
    const current = parseScanInput(scanInput) ?? SCAN_MIN;
    applyCount(current - 1);
  }, [applyCount, scanInput]);

  const handleIncrement = useCallback(() => {
    const current = parseScanInput(scanInput) ?? SCAN_MIN;
    applyCount(current + 1);
  }, [applyCount, scanInput]);

  const handleBlur = useCallback(() => {
    const parsed = parseScanInput(scanInput);
    if (parsed == null) {
      setInputError(`Enter a whole number between ${SCAN_MIN} and ${SCAN_MAX}.`);
      return;
    }
    if (parsed < SCAN_MIN || parsed > SCAN_MAX) {
      setInputError(`Enter a whole number between ${SCAN_MIN} and ${SCAN_MAX}.`);
      applyCount(parsed);
      return;
    }
    setInputError(null);
    setScanInput(String(parsed));
  }, [applyCount, scanInput]);

  const handleBuy = useCallback((provider: PaymentProvider) => {
    const parsed = parseScanInput(scanInput);
    if (parsed == null) {
      setInputError(`Enter a whole number between ${SCAN_MIN} and ${SCAN_MAX}.`);
      return;
    }
    if (parsed < SCAN_MIN || parsed > SCAN_MAX) {
      setInputError(`Enter a whole number between ${SCAN_MIN} and ${SCAN_MAX}.`);
      return;
    }
    setInputError(null);
    onPayPack(parsed, provider);
  }, [onPayPack, scanInput]);

  const atMin = (parseScanInput(scanInput) ?? SCAN_MIN) <= SCAN_MIN;
  const atMax = (parseScanInput(scanInput) ?? SCAN_MAX) >= SCAN_MAX;

  return (
    <>
      <span className="eyebrow">Multiple scans</span>
      <h1>Buy scan credits</h1>
      <p className="family-pack-page-lede">
        Each scan costs USD $3 till our official launch in Q4.
        Buy 2–10 scans — use them yourself or share with family and friends from your credits page.
      </p>

      <div className="family-pack-custom family-pack-buy-single">
        <div className="family-pack-scan-row">
          <span className="family-pack-scan-label">Number of scans</span>
          <div className="scan-count-stepper">
            <button
              type="button"
              className="scan-count-stepper-btn"
              aria-label="Decrease scan count"
              disabled={paying || atMin}
              onClick={handleDecrement}
            >
              <Minus size={18} aria-hidden />
            </button>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="scan-count-stepper-input"
              value={scanInput}
              disabled={paying}
              aria-label="Number of scans"
              aria-invalid={!!inputError}
              onChange={(e) => {
                setScanInput(e.target.value.replace(/[^\d]/g, ''));
                setInputError(null);
              }}
              onBlur={handleBlur}
            />
            <button
              type="button"
              className="scan-count-stepper-btn"
              aria-label="Increase scan count"
              disabled={paying || atMax}
              onClick={handleIncrement}
            >
              <Plus size={18} aria-hidden />
            </button>
          </div>
          <span className="family-pack-custom-price">
            {displayPrice != null ? `USD $${displayPrice}` : '—'}
          </span>
        </div>

        <p className="family-pack-custom-hint">
          {SCAN_MIN}–{SCAN_MAX} scans at USD $3 each
        </p>

        <AdhocPaymentMethodButtons paying={paying} onPay={handleBuy} />
      </div>

      {(inputError || payError) && (
        <p className="family-pack-error" role="alert">
          {inputError || payError}
        </p>
      )}
    </>
  );
};

export default AdhocFamilyPackSection;
